import type { PropertyValueMap } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type * as Y from 'yjs';
import { DEditTextarea } from './d-edit-textarea.js';

/**
 * A textarea component with built-in Yjs Text binding for real-time collaboration
 * and CRDT support. Extends d-edit-textarea with Yjs integration.
 *
 * @fires yjs-content-changed - Dispatched when content changes via Yjs
 */
@customElement('d-yjs-textarea')
export class DYjsTextarea extends DEditTextarea {
  @property({ type: Object, attribute: false })
  yjsText?: Y.Text;

  private isUpdatingFromYjs = false;

  connectedCallback() {
    super.connectedCallback();
    this.bindYjsText();
  }

  disconnectedCallback() {
    this.unbindYjsText();
    super.disconnectedCallback();
  }

  updated(changedProperties: PropertyValueMap<this>) {
    super.updated(changedProperties);
    if (changedProperties.has('yjsText')) {
      // Unbind from the old yjsText if it existed
      const oldYjsText = changedProperties.get('yjsText') as Y.Text | undefined;
      const newYjsText = this.yjsText;

      console.log('[YjsTextarea] yjsText property changed:', {
        hadOld: !!oldYjsText,
        oldContent: oldYjsText?.toString(),
        hasNew: !!newYjsText,
        newContent: newYjsText?.toString(),
      });

      if (oldYjsText) {
        try {
          oldYjsText.unobserve(this.handleYjsChange);
          console.log('[YjsTextarea] Unobserved old Y.Text');
        } catch (_e) {
          console.log('[YjsTextarea] Could not unobserve old Y.Text (already removed)');
        }
      }
      // Bind to the new yjsText
      this.bindYjsText();
    }
  }

  private bindYjsText() {
    if (!this.yjsText) {
      console.log('[YjsTextarea] bindYjsText called but no yjsText provided');
      return;
    }

    // Set initial value from Yjs
    this.value = this.yjsText.toString();
    console.log('[YjsTextarea] Bound to new Y.Text, initial value:', this.value);

    // Listen to Yjs changes
    this.yjsText.observe(this.handleYjsChange);
    console.log('[YjsTextarea] Observing Y.Text for changes');
  }

  private unbindYjsText() {
    if (!this.yjsText) return;
    try {
      this.yjsText.unobserve(this.handleYjsChange);
    } catch (_e) {
      // Ignore if handler was already removed
    }
  }

  private handleYjsChange = (event: Y.YTextEvent, transaction: Y.Transaction) => {
    // Ignore changes that originated from this component
    if (transaction.local) return;

    this.isUpdatingFromYjs = true;
    this.value = this.yjsText!.toString();
    this.isUpdatingFromYjs = false;

    // Dispatch custom event for Yjs content changes
    this.dispatchEvent(
      new CustomEvent('yjs-content-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.value, event, transaction },
      }),
    );
  };

  inputHandler(event: Event) {
    // Skip if this change came from Yjs
    if (this.isUpdatingFromYjs) return;

    const target = event.target as HTMLTextAreaElement;
    const newValue = target.value;
    const oldValue = this.value;

    // Update local value
    this.value = newValue;

    // Update Yjs if bound
    if (this.yjsText && oldValue !== newValue) {
      // Calculate the diff and apply minimal changes
      const doc = this.yjsText.doc;
      if (doc) {
        doc.transact(() => {
          // Simple replacement for now - could be optimized with proper diff
          this.yjsText!.delete(0, this.yjsText!.length);
          this.yjsText!.insert(0, newValue);
        }, this); // Pass 'this' as origin to identify local changes
      }
    }

    // Dispatch the regular value-changed event
    this.dispatchEvent(
      new CustomEvent('value-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.value },
      }),
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'd-yjs-textarea': DYjsTextarea;
  }
}
